/**
 * Settings -> Roles: the people seated in this CRM.
 *
 * It sits beside the role editor rather than in a tab of its own because the
 * two answer halves of one question. The card above says what a role may do;
 * this one says who holds it. Splitting them puts a person's rights two clicks
 * from the person.
 *
 * WHAT THIS FIXES. Until now nothing anywhere could create a user - accounts
 * only ever arrived through self-service signup, so adding a colleague meant
 * asking them to sign up and then hunting for their row in the platform admin.
 * That is also why the Perfex import leaves owners empty: it has five staff
 * members in the old system and no platform account to point any of them at.
 *
 * NOBODY TYPES SOMEBODY ELSE'S PASSWORD. Creating a person makes an account
 * with no password and mails them a link to set their own. The row says
 * "invited" until they do, so an invitation that never arrived is visible
 * rather than a colleague who quietly cannot get in.
 *
 * SWITCHING OFF, NOT DELETING. A person's id is written across project members,
 * task assignees, ticket owners and every hour they logged. Removing the row
 * turns all of that into ids that resolve to nothing, which draws as a bug
 * rather than as somebody who has left.
 */
import { useCallback, useEffect, useState } from "react";
import { Loader2, MailCheck, UserPlus } from "lucide-react";
import { useI18n, useT } from "../../../../i18n/I18nProvider";
import {
  createCrmPerson,
  getCrmPeople,
  inviteCrmPerson,
  updateCrmPerson,
  type CrmPeopleResponse,
  type CrmPerson,
} from "../../../../lib/crmPeople";
import { Avatar, Btn, Card, CardHead, EmptyState, SelectField, StatusPill, useToast } from "../../../ui";
import { AddPersonModal } from "./AddPersonModal";

export function PeopleCard() {
  const t = useT();
  const { locale } = useI18n();
  const toast = useToast();

  const [data, setData] = useState<CrmPeopleResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  // The id currently being written, so one row's spinner does not freeze the
  // whole list while somebody changes a role.
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await getCrmPeople());
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function patch(person: CrmPerson, body: { roleId?: string; active?: boolean }) {
    setBusyId(person.id);
    try {
      await updateCrmPerson(person.id, body);
      await load();
    } catch {
      toast(t("crmPeople.saveError"));
    } finally {
      setBusyId(null);
    }
  }

  async function invite(person: CrmPerson) {
    setBusyId(person.id);
    try {
      const res = await inviteCrmPerson(person.id, locale);
      // "Throttled" is not a failure and must not be reported as success
      // either: the earlier mail is still the live one, and telling somebody a
      // second was sent would have them looking for a message that is not
      // coming.
      toast(res.sent ? t("crmPeople.inviteSent", { email: res.sentTo }) : t("crmPeople.inviteRecent"));
    } catch {
      toast(t("crmPeople.inviteError"));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <Card>
      <CardHead
        title={t("crmPeople.title")}
        sub={t("crmPeople.body")}
        actions={
          <Btn tone="dark" onClick={() => setAdding(true)} disabled={loading || !data}>
            {t("crmPeople.add")}
          </Btn>
        }
      />

      {loading ? (
        <div className="flex justify-center py-10 text-muted-foreground">
          <Loader2 className="size-5 animate-spin" />
        </div>
      ) : !data || data.rows.length === 0 ? (
        <EmptyState icon={UserPlus} title={t("crmPeople.emptyTitle")} body={t("crmPeople.empty")} />
      ) : (
        <div className="crm-rows">
          {data.rows.map((p) => (
            <div
              key={p.id}
              className="crm-list-row-soft grid items-center gap-3 sm:grid-cols-[1.5fr_auto_1fr_auto]"
            >
              <div className="flex min-w-0 items-center gap-2">
                <Avatar name={p.name} src={p.avatarUrl} />
                <div className="min-w-0">
                  <div className="crm-prow-n crm-trunc">{p.name}</div>
                  <div className="crm-prow-s crm-trunc">{p.email}</div>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                {p.pending ? (
                  <StatusPill tone="wt">{t("crmPeople.pending")}</StatusPill>
                ) : !p.active ? (
                  <StatusPill tone="mut">{t("crmPeople.off")}</StatusPill>
                ) : (
                  <StatusPill tone="ok">{t("crmPeople.on")}</StatusPill>
                )}
              </div>

              <SelectField
                value={p.roleId ?? ""}
                disabled={busyId === p.id}
                onChange={(next) => void patch(p, { roleId: next })}
              >
                {data.roles.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
              </SelectField>

              <div className="flex items-center gap-2">
                {p.pending ? (
                  <Btn tone="plain" onClick={() => void invite(p)} disabled={busyId === p.id}>
                    <MailCheck className="size-4" />
                    {t("crmPeople.resend")}
                  </Btn>
                ) : null}
                <Btn
                  tone="plain"
                  onClick={() => void patch(p, { active: !p.active })}
                  disabled={busyId === p.id}
                >
                  {p.active ? t("crmPeople.turnOff") : t("crmPeople.turnOn")}
                </Btn>
              </div>
            </div>
          ))}
        </div>
      )}

      {adding && data ? (
        <AddPersonModal
          roles={data.roles}
          onClose={() => setAdding(false)}
          onDone={async (result) => {
            setAdding(false);
            // Two independent facts, and the message has to carry both or it
            // lies. "Found and seated" differs from "created", and an invitation
            // that was not asked for must not be reported as sent - the first
            // version of this said "added and invited" with the box unticked,
            // which sends somebody looking for a mail that is not coming.
            const invited = result.invited === "sent";
            toast(
              result.reused
                ? invited
                  ? t("crmPeople.seatedInvited")
                  : t("crmPeople.seated")
                : invited
                  ? t("crmPeople.created")
                  : t("crmPeople.createdNoInvite"),
            );
            await load();
          }}
          submit={(body) => createCrmPerson({ ...body, locale })}
        />
      ) : null}
    </Card>
  );
}
